import { useMemo } from "react";

import { isNotNullOrUndefined } from "@smart/itops-utils-basic";
import {
  useLoadMatterPermisions,
  useLoadMatterTypes,
} from "@smart/manage-gql-client-dom";

import { GqlSubmissionSummary } from "../../types";

export const useSubmissionsWithMatterAccess = (
  submissions: GqlSubmissionSummary[] | null,
) => {
  const matterIds = [
    ...new Set(
      submissions
        ?.map((submission) => submission.values.matterId)
        .filter(isNotNullOrUndefined),
    ),
  ];
  const matterPermissions = useLoadMatterPermisions(
    { matterIds },
    { skip: !matterIds.length },
  );

  const noPermissionsMatterIds = matterPermissions.result
    ?.filter((p) => !p.canAccess)
    .map((p) => p.matterId);

  return {
    submissions: submissions?.filter(
      (s) =>
        !s.values.matterId ||
        !noPermissionsMatterIds?.includes(s.values.matterId),
    ),
    loadingMatterPermissions: matterPermissions.loading,
    noPermissionsMatterIds,
  };
};

export const useSubmissionsMatterTypes = (
  submissions: GqlSubmissionSummary[] | null,
) => {
  const [leadMatterTypeIds, matterMatterTypeIds] = useMemo(() => {
    const matterTypeIds = submissions?.reduce<{
      lead: string[];
      matter: string[];
    }>(
      (aggr, s) => {
        if (!s.matter) return aggr;
        aggr[s.matter.isLead ? "lead" : "matter"].push(s.matter.matterTypeId);
        return aggr;
      },
      { lead: [], matter: [] },
    );
    return [
      matterTypeIds?.lead.length ? [...new Set(matterTypeIds.lead)] : [],
      matterTypeIds?.matter.length ? [...new Set(matterTypeIds.matter)] : [],
    ];
  }, [submissions]);

  const leadMatterTypes = useLoadMatterTypes(
    { matterTypeIds: leadMatterTypeIds, type: "lead" },
    { skip: !leadMatterTypeIds.length },
  );
  const matterMatterTypes = useLoadMatterTypes(
    { matterTypeIds: matterMatterTypeIds, type: "matter" },
    { skip: !matterMatterTypeIds.length },
  );

  return {
    leadMatterTypes: leadMatterTypes.result,
    matterMatterTypes: matterMatterTypes.result,
    loadingMatterTypes: leadMatterTypes.loading || matterMatterTypes.loading,
  };
};
